import { useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { SortSelect } from '@/shared/components/sort-select';
import { useQuery } from '@tanstack/react-query';
import { usePassbook } from '@/shared/lib/passbook';
import { useSelection } from '@/shared/lib/use-selection';
import { PageHeader } from '@/shared/components/page-header';
import { SelectableListLayout } from '@/shared/components/selectable-list-layout';
import { Button } from '@/shared/ui/button';
import { Pagination } from '@/shared/ui/pagination';
import type { MetabaseCampaign } from '@/shared/types/campaign';
import { fetchCampaigns } from '../api/use-campaigns';
import { UploadCampaignCard } from './campaign-card';
import { UploadCampaignFormDialog } from './upload-campaign-form-dialog';

const PAGE_SIZE = 25;

export function UploadCampaignsPage() {
  const { idPassbook, passbook } = usePassbook();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [sortValue, setSortValue] = useState('date:desc');
  const [createOpen, setCreateOpen] = useState(false);

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['campaigns', 'upload', idPassbook],
    queryFn: () => fetchCampaigns(idPassbook, 'upload') as Promise<MetabaseCampaign[]>,
    staleTime: 60_000,
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  const filtered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    const result = q
      ? data.filter((c) => {
          const msg = (c.description ?? '').toLowerCase();
          return (c.name ?? '').toLowerCase().includes(q) || msg.includes(q);
        })
      : [...data];

    if (sortValue === 'name:asc') result.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
    else if (sortValue === 'name:desc') result.sort((a, b) => (b.name ?? '').localeCompare(a.name ?? ''));
    else if (sortValue === 'date:asc') result.sort((a, b) => (a.updated ?? '').localeCompare(b.updated ?? ''));
    else result.sort((a, b) => (b.updated ?? '').localeCompare(a.updated ?? ''));

    return result;
  }, [data, debouncedSearch, sortValue]);

  useEffect(() => {
    setPageIndex(0);
  }, [debouncedSearch, sortValue]);

  const pageRows = useMemo(() => {
    const start = pageIndex * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, pageIndex]);

  const selection = useSelection(pageRows);

  useEffect(() => {
    selection.clear();
  }, [pageIndex, debouncedSearch]);

  return (
    <>
      <PageHeader
        title="Upload de lista"
        hideMetaOnMobile
        description={passbook.organizationName}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="size-4" /> Nova campanha
          </Button>
        }
      />

      <SelectableListLayout
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Buscar por nome ou mensagem…"
        toolbarExtra={
          <SortSelect
            value={sortValue}
            onChange={setSortValue}
            options={[
              { value: 'date:desc', label: 'Mais recentes' },
              { value: 'date:asc', label: 'Mais antigas' },
              { value: 'name:asc', label: 'Nome A→Z' },
              { value: 'name:desc', label: 'Nome Z→A' },
            ]}
          />
        }
        selectAllId="select-all-campaigns-upload"
        itemCount={pageRows.length}
        allOnPageSelected={selection.allOnPageSelected}
        someOnPageSelected={selection.someOnPageSelected}
        onToggleAllOnPage={selection.toggleAllOnPage}
        isLoading={isLoading}
        isEmpty={!isLoading && filtered.length === 0}
        emptyTitle="Nenhuma campanha"
        emptyDescription="Faça upload de um arquivo exportado do BI para criar campanhas."
        footer={
          filtered.length > PAGE_SIZE ? (
            <Pagination
              page={pageIndex}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPageIndex}
              className="justify-center pt-2"
            />
          ) : null
        }
      >
        {pageRows.map((campaign, index) => (
          <li key={campaign._id}>
            <UploadCampaignCard
              campaign={campaign}
              index={index}
              selected={selection.selectedIds.has(campaign._id)}
              onToggle={() => selection.toggle(campaign._id)}
            />
          </li>
        ))}
      </SelectableListLayout>

      <UploadCampaignFormDialog
        idPassbook={idPassbook}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => refetch()}
      />
    </>
  );
}
